/**
 * Crisp live-chat widget — the ONLY client-side vendor touch point (the agent
 * inbox / ticket REST API is server-only, behind SupportClient → the backend).
 * Used when the assistant escalates to a human: we preload the user + context
 * into the widget and open it so the user lands in a live conversation. When no
 * agent is online Crisp collects it as an async ticket (email follow-up) — all
 * handled by Crisp, not us.
 *
 * Uses the stable `$crisp` command-queue embed (https://client.crisp.chat/l.js)
 * rather than an npm wrapper: zero dependencies, lazy-loaded only when first
 * needed (kept off the critical path), and a no-op when VITE_CRISP_WEBSITE_ID
 * is unset (local/dev/OSS).
 */

type CrispCommand = unknown[];

declare global {
  interface Window {
    $crisp?: CrispCommand[];
    CRISP_WEBSITE_ID?: string;
  }
}

const WEBSITE_ID = (import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined) ?? "";

let loaded = false;

/** True when a website id is configured — callers can branch (e.g. fall back to
 *  the async bug-report form if live chat isn't available). */
export function isCrispAvailable(): boolean {
  return WEBSITE_ID.length > 0;
}

const CRISP_SRC = "https://client.crisp.chat/l.js";

/** Lazily injects the Crisp embed once. Safe to call repeatedly. */
export function loadCrisp(): void {
  if (loaded || !isCrispAvailable() || typeof document === "undefined") return;
  loaded = true;
  window.$crisp = window.$crisp ?? [];
  window.CRISP_WEBSITE_ID = WEBSITE_ID;
  // Guard against a second <script> (Vite HMR resets `loaded`; a stray tag would
  // load l.js twice → duplicate widget / double queue replay).
  if (document.querySelector(`script[src="${CRISP_SRC}"]`)) return;
  const script = document.createElement("script");
  script.src = CRISP_SRC;
  script.async = true;
  // If the embed is blocked (ad-block / CSP / offline) the widget will never
  // open. We can't recover the live channel here, but the escalate handler
  // always also files a server-side ticket, so the user is not stranded — log
  // for visibility.
  script.onerror = () => {
    // eslint-disable-next-line no-console
    console.warn("[crisp] live-chat embed failed to load; live widget unavailable");
  };
  document.head.appendChild(script);
}

function push(command: CrispCommand): void {
  if (!isCrispAvailable()) return;
  loadCrisp();
  (window.$crisp = window.$crisp ?? []).push(command);
}

export type CrispContext = {
  email?: string | null;
  nickname?: string | null;
  segments?: string[];
  data?: Record<string, string>;
};

/** Sets the visitor identity + session data/segments on the widget. */
export function setCrispContext(ctx: CrispContext): void {
  if (ctx.email) push(["set", "user:email", [ctx.email]]);
  if (ctx.nickname) push(["set", "user:nickname", [ctx.nickname]]);
  if (ctx.segments && ctx.segments.length > 0) {
    push(["set", "session:segments", [ctx.segments]]);
  }
  if (ctx.data) {
    const pairs = Object.entries(ctx.data).filter(([, v]) => v != null && v !== "");
    if (pairs.length > 0) push(["set", "session:data", [pairs]]);
  }
}

/** Opens the chat, optionally prefilling the composer with a summary line. */
export function openCrispWithSummary(text?: string): void {
  if (text && text.trim()) push(["set", "message:text", [text]]);
  push(["do", "chat:show"]);
  push(["do", "chat:open"]);
}
